import { ArticleNumberAllowance } from '../numbering/ArticleNumberAllowance';
import {
  Box,
  Checkbox,
  FormControl,
  Flex,
  FormErrorMessage,
  Input,
  Select,
  Text,
} from '@chakra-ui/react';
import { AngularContactsModal } from '@texas/components/customer/contacts/AngularContactsModal';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { createArticleStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { useTranslation } from 'react-i18next';
import { ManagementCard } from '../manage/ManagementCard';
import { useCreateArticle } from './useCreateArticle';
import { CreateArticleNumbering } from './CreateArticleNumbering';
import { articleDescriptionMaxLength } from '@texas/resources/constants';

export function CreateArticleConfiguration() {
  const { t } = useTranslation();
  const createArticle = useCreateArticle();
  return (
    <ManagementCard
      className="create-article-card"
      title={t('article.create.configuration')}
    >
      <Box sx={styles.stack}>
        <FormControl
          isRequired={true}
          id={createArticle.fieldWarnings.template.targetId}
        >
          <WarningComponent {...createArticle.fieldWarnings.template}>
            <TexasFormLabel>
              {t('configuration.articleTemplate')}
            </TexasFormLabel>
          </WarningComponent>
          <Select
            value={createArticle.template?.id ?? ''}
            isDisabled={createArticle.resources.templates.loading}
            placeholder={t('general.select')}
            onChange={(event) =>
              createArticle.selectTemplate(Number(event.target.value))
            }
          >
            {(createArticle.resources.templates.data ?? [])
              .filter((item) => !item.archived)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </Select>
        </FormControl>
        <Box sx={styles.fields}>
          <FormControl
            isRequired={true}
            isInvalid={createArticle.itemCodeLengthInvalid}
            id={createArticle.fieldWarnings.itemCode.targetId}
          >
            <WarningComponent {...createArticle.fieldWarnings.itemCode}>
              <TexasFormLabel>{t('general.itemCode')}</TexasFormLabel>
            </WarningComponent>
            <Input
              variant="texas"
              value={createArticle.itemCode}
              maxLength={createArticle.itemCodeInputMaxLength}
              isDisabled={!createArticle.template}
              onChange={(e) =>
                createArticle.setValue('itemCode', e.target.value)
              }
            />
            <FormCounter
              length={createArticle.itemCode.length}
              maxLength={createArticle.itemCodeInputMaxLength}
            />
            {createArticle.template && (
              <ArticleNumberAllowance
                maximumLength={createArticle.itemCodeMaxLength}
                reservedDigits={createArticle.rangeNumberLength}
                inputMaxLength={createArticle.itemCodeInputMaxLength}
              />
            )}
            <FormErrorMessage>
              {t('article.copy.itemCodeLengthRequirement', {
                minimum: createArticle.itemCodeMinLength,
                maximum: createArticle.itemCodeMaxLength,
              })}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            isRequired={true}
            id={createArticle.fieldWarnings.description.targetId}
          >
            <WarningComponent {...createArticle.fieldWarnings.description}>
              <TexasFormLabel>{t('general.description')}</TexasFormLabel>
            </WarningComponent>
            <Input
              variant="texas"
              value={createArticle.name}
              maxLength={articleDescriptionMaxLength}
              onChange={(e) => createArticle.setValue('name', e.target.value)}
            />
            <FormCounter
              length={createArticle.name.length}
              maxLength={articleDescriptionMaxLength}
            />
          </FormControl>
        </Box>
        <CreateArticleNumbering />
        <FormControl>
          <TexasFormLabel>{t('article.create.brandContact')}</TexasFormLabel>
          <Text sx={styles.muted}>{t('article.create.brandContactHint')}</Text>
          <Flex gap={3} align="center">
            <Select
              flex={1}
              minW={0}
              value={createArticle.contactId ?? ''}
              isDisabled={!createArticle.customerId}
              onChange={(e) =>
                createArticle.setValue(
                  'contactId',
                  Number(e.target.value) || null,
                )
              }
            >
              <option value="">{t('general.none')}</option>
              {(createArticle.resources.contacts.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            {createArticle.customerId > 0 && (
              <AngularContactsModal customerId={createArticle.customerId} />
            )}
          </Flex>
        </FormControl>
        <Box sx={styles.row}>
          <Checkbox
            isChecked={createArticle.variable}
            onChange={(e) =>
              createArticle.setValue('variable', e.target.checked)
            }
          >
            {t('article.create.vdc')}
          </Checkbox>
          <Checkbox
            isChecked={createArticle.collectionItem}
            onChange={(e) =>
              createArticle.setValue('collectionItem', e.target.checked)
            }
          >
            {t('article.create.collectionItem')}
          </Checkbox>
        </Box>
      </Box>
    </ManagementCard>
  );
}
